/* GroupFoodCostTile — group-wide F&B cost rollup across all 5 outlets.
 *
 * Shows on the Group Sales page so Dipali can spot leakage at the
 * group level without drilling into each outlet. Click → Outlet
 * Intelligence Cost tab for the worst-performing outlet.
 *
 * Doctrine (locked 04-May-2026):
 *  - Cost % = (food + beverage) / revenue · industry standard
 *  - Consumables (gas, cleaning, packaging) are a SEPARATE P&L bucket
 *  - <30% green · 30-35% amber · ≥35% red
 */
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Percent, AlertTriangle, TrendingUp, TrendingDown, ChefHat, ArrowRight } from 'lucide-react';

const INR_SHORT = (n) => {
  const v = Number(n || 0);
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(2)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(2)}L`;
  return `₹${v.toLocaleString('en-IN')}`;
};

const HEALTH_COLOR = {
  green: '#10B981',
  amber: '#F59E0B',
  red: '#DC2626',
  no_data: '#9CA3AF',
};

const RANGE_OPTIONS = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: 'mtd', label: 'MTD' },
  { id: '90d', label: '90d' },
];

export default function GroupFoodCostTile() {
  const navigate = useNavigate();
  const [range, setRange] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const r = await api.get(`/cost-intel/group/summary?range=${range}`);
        if (!cancelled) setData(r.data);
      } catch (e) {
        console.error(e);
        if (!cancelled) setError(e?.response?.data?.detail || 'Failed to load');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [range]);

  if (loading) {
    return (
      <div className="rounded-xl bg-white p-5" style={{ border: '1px solid #E5E1DC' }}>
        <p className="text-sm" style={{ color: '#6B7280' }}>Loading group F&amp;B cost…</p>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="rounded-xl bg-white p-5" style={{ border: '1px solid #E5E1DC' }}>
        <p className="text-sm" style={{ color: '#6B7280' }}>
          Group F&amp;B cost unavailable: {error || 'no data'}
        </p>
      </div>
    );
  }

  const g = data.group;
  const fg = HEALTH_COLOR[g.health] || '#1B2A4A';
  const goAlert = g.health === 'red' || g.health === 'amber';
  const TrendIcon = goAlert ? TrendingUp : TrendingDown;

  return (
    <div
      className="rounded-2xl p-5"
      data-testid="group-food-cost-tile"
      style={{
        background: 'linear-gradient(135deg, #1B2A4A 0%, #2A3F66 100%)',
        color: 'white',
        border: '1px solid #1B2A4A',
      }}
    >
      {/* Header */}
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <ChefHat size={14} color="#9DB5CC" />
            <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: '#9DB5CC' }}>
              Group · Food &amp; Beverage Cost
            </p>
          </div>
          <p className="text-[11px]" style={{ color: '#9DB5CC' }}>
            All 5 outlets · {data.from_date} → {data.to_date}
          </p>
        </div>
        <div className="flex items-center gap-1">
          {RANGE_OPTIONS.map(o => (
            <button
              key={o.id}
              onClick={() => setRange(o.id)}
              data-testid={`group-cost-range-${o.id}`}
              className="px-2.5 py-0.5 text-[10px] rounded-full"
              style={{
                background: range === o.id ? 'white' : 'transparent',
                color: range === o.id ? '#1B2A4A' : '#9DB5CC',
                border: range === o.id ? 'none' : '1px solid #3B5278',
              }}
            >
              {o.label}
            </button>
          ))}
        </div>
      </div>

      {/* Hero number */}
      <div className="flex items-end gap-6 mb-4">
        <div>
          <div className="flex items-baseline gap-2">
            <Percent size={24} color={fg} />
            <span className="text-5xl font-semibold tabular-nums" style={{ color: fg }} data-testid="group-cost-pct">
              {g.food_cost_pct}%
            </span>
          </div>
          <p className="text-[11px] mt-1" style={{ color: '#9DB5CC' }}>
            Food + Beverage / Revenue · industry std
          </p>
        </div>
        <div className="flex-1 grid grid-cols-3 gap-3">
          <MiniStat
            label="Revenue"
            value={INR_SHORT(g.revenue)}
            sub={`${data.outlet_count} outlets`}
            testid="group-revenue"
          />
          <MiniStat
            label="Food + Bev"
            value={INR_SHORT(g.food + g.beverage)}
            sub={`Food ${INR_SHORT(g.food)} · Bev ${INR_SHORT(g.beverage)}`}
            testid="group-food-bev"
          />
          <MiniStat
            label="Consumables"
            value={INR_SHORT(g.consumable)}
            sub={`${g.consumable_pct}% · separate P&L`}
            testid="group-consumables"
          />
        </div>
      </div>

      {/* Per-outlet bars */}
      <div className="space-y-1.5 mb-4">
        {data.outlets.map(o => {
          const tone = HEALTH_COLOR[o.health] || '#9CA3AF';
          const noData = o.health === 'no_data';
          return (
            <div
              key={o.outlet_id}
              onClick={() => navigate(`/outlet-intelligence?outlet=${o.outlet_id}&tab=cost`)}
              data-testid={`group-outlet-row-${o.outlet_id}`}
              className="flex items-center gap-3 text-[12px] py-1.5 px-2 rounded-md cursor-pointer transition-colors"
              style={{ background: 'rgba(255,255,255,0.03)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.08)'}
              onMouseLeave={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
            >
              <span className="flex-1 truncate" style={{ color: '#E5E7EB' }}>{o.outlet_name}</span>
              <span className="w-20 text-right tabular-nums" style={{ color: '#9DB5CC' }}>
                {INR_SHORT(o.revenue)}
              </span>
              <span className="rounded-full h-1.5 w-32" style={{ backgroundColor: 'rgba(255,255,255,0.1)' }}>
                <span
                  className="block h-1.5 rounded-full"
                  style={{
                    width: `${noData ? 0 : Math.min(o.food_cost_pct * 2, 100)}%`,
                    backgroundColor: tone,
                  }}
                />
              </span>
              <span className="w-12 text-right tabular-nums font-semibold" style={{ color: tone }}>
                {noData ? '—' : `${o.food_cost_pct}%`}
              </span>
              <ArrowRight size={11} color="#6B7280" />
            </div>
          );
        })}
      </div>

      {/* Worst / best callout */}
      {data.worst && (
        <div
          className="rounded-lg px-3 py-2 text-[11px] flex items-center gap-2"
          style={{
            background: goAlert ? 'rgba(220,38,38,0.12)' : 'rgba(16,185,129,0.12)',
            color: '#E5E7EB',
            border: `1px solid ${goAlert ? '#7F1D1D' : '#065F46'}33`,
          }}
        >
          <AlertTriangle size={12} color={goAlert ? '#FCA5A5' : '#A7F3D0'} />
          <span>
            <strong style={{ color: HEALTH_COLOR[data.worst.health] }}>
              {data.worst.outlet_name}
            </strong>
            {' '}has the highest F&amp;B cost at {data.worst.food_cost_pct}% —
            {data.best && data.best.outlet_id !== data.worst.outlet_id && (
              <> compare against {data.best.outlet_name} ({data.best.food_cost_pct}%) for what good looks like.</>
            )}
          </span>
          <TrendIcon size={12} color={goAlert ? '#FCA5A5' : '#A7F3D0'} className="ml-auto flex-shrink-0" />
        </div>
      )}
    </div>
  );
}


function MiniStat({ label, value, sub, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[9px] uppercase tracking-wider" style={{ color: '#9DB5CC' }}>{label}</p>
      <p className="text-base font-semibold tabular-nums" style={{ color: 'white' }}>{value}</p>
      <p className="text-[10px] mt-0.5" style={{ color: '#7B8FA8' }}>{sub}</p>
    </div>
  );
}
